import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Alert,
  Stepper, Step, StepLabel, CircularProgress
} from '@mui/material';
import { Send, NavigateNext, NavigateBefore, HowToReg } from '@mui/icons-material';
import { formsAPI, draftsAPI, captchaAPI, scholarAPI, registrationAPI } from '../../services/api';
import { MultiStepFormRenderer } from '../../components/FormRenderer';

export default function RegistrationForm() {
  var params = useParams();
  var formId = params.formId;

  // Form data
  var fs = useState(null); var form = fs[0]; var setForm = fs[1];
  var ss = useState(null); var schema = ss[0]; var setSchema = ss[1];
  var ls = useState(true); var loading = ls[0]; var setLoading = ls[1];
  var es = useState(''); var error = es[0]; var setError = es[1];
  var ps = useState({}); var parsedSettings = ps[0]; var setParsedSettings = ps[1];

  // OTP flow
  var ns = useState(''); var name = ns[0]; var setName = ns[1];
  var ems = useState(''); var email = ems[0]; var setEmail = ems[1];
  var otps = useState(''); var otp = otps[0]; var setOtp = otps[1];
  var oes = useState(''); var otpError = oes[0]; var setOtpError = oes[1];
  var ovs = useState(false); var otpVerified = ovs[0]; var setOtpVerified = ovs[1];
  var oss = useState(false); var otpSent = oss[0]; var setOtpSent = oss[1];
  var ols = useState(false); var otpLoading = ols[0]; var setOtpLoading = ols[1];

  // Scholar status
  var scs = useState(null); var scholarStatus = scs[0]; var setScholarStatus = scs[1];
  var isScholar = scholarStatus && scholarStatus.is_scholar;

  // Form filling
  var vs = useState({}); var values = vs[0]; var setValues = vs[1];
  var fes = useState({}); var fieldErrors = fes[0]; var setFieldErrors = fes[1];
  var cst = useState(0); var currentStep = cst[0]; var setCurrentStep = cst[1];
  var sbs = useState(false); var submitting = sbs[0]; var setSubmitting = sbs[1];
  var sbd = useState(false); var submitted = sbd[0]; var setSubmitted = sbd[1];
  var dts = useState(''); var draftToken = dts[0]; var setDraftToken = dts[1];

  // CAPTCHA
  var cqs = useState(''); var captchaQuestion = cqs[0]; var setCaptchaQuestion = cqs[1];
  var cts = useState(''); var captchaToken = cts[0]; var setCaptchaToken = cts[1];
  var cas = useState(''); var captchaAnswer = cas[0]; var setCaptchaAnswer = cas[1];
  var ces = useState(false); var captchaEnabled = ces[0]; var setCaptchaEnabled = ces[1];

  var autoSaveTimer = useRef(null);

  // Load form
  useEffect(function() {
    formsAPI.getPublic(formId)
      .then(function(r) {
        var f = r.data.data;
        if (f.form_type !== 'registration') {
          setError('This is not a registration form. Use /apply/' + formId + ' instead.');
          setLoading(false);
          return;
        }
        setForm(f);
        var s = typeof f.schema === 'string' ? JSON.parse(f.schema) : f.schema;
        setSchema(s);
        var settings = typeof f.settings === 'string' ? JSON.parse(f.settings) : (f.settings || {});
        setParsedSettings(settings);
        setCaptchaEnabled(!!settings.enable_captcha);
        if (settings.enable_captcha) {
          captchaAPI.generate().then(function(cr) {
            setCaptchaQuestion(cr.data.data.question);
            setCaptchaToken(cr.data.data.token);
          });
        }
        setLoading(false);
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || 'Form not found');
        setLoading(false);
      });
  }, [formId]);

  // Check scholar status
  function checkScholarAndSendOTP() {
    setOtpLoading(true);
    setOtpError('');

    // First check scholar status
    scholarAPI.checkStatus(formId, email)
      .then(function(r) {
        var status = r.data.data;
        setScholarStatus(status);

        // Now send OTP (the backend SendOTP will also check for duplicates)
        return draftsAPI.sendOTP(formId, email, name);
      })
      .then(function() {
        setOtpSent(true);
        setOtpLoading(false);
      })
      .catch(function(err) {
        var msg = (err.response && err.response.data && err.response.data.error) || 'Failed';
        setOtpError(msg);
        setOtpLoading(false);
      });
  }

  // Verify OTP
  function verifyOTP() {
    setOtpLoading(true);
    draftsAPI.verifyOTP(formId, email, otp)
      .then(function(r) {
        setOtpVerified(true);
        setDraftToken(r.data.data.draft_token);
        setOtpLoading(false);
        // Best-effort restore of a previously saved draft (name + responses)
        draftsAPI.loadDraft(formId, r.data.data.draft_token).then(function(dr) {
          var d = dr.data.data;
          if (d && d.name) setName(d.name);
          if (d && d.responses) {
            var resp = typeof d.responses === 'string' ? JSON.parse(d.responses) : d.responses;
            if (resp) setValues(resp);
          }
        }).catch(function(e2) { /* no saved draft, start fresh */ });
      })
      .catch(function(err) {
        setOtpError((err.response && err.response.data && err.response.data.error) || 'Verification failed');
        setOtpLoading(false);
      });
  }

  // Field change
  function handleFieldChange(fieldId, value) {
    setValues(function(prev) {
      var next = Object.assign({}, prev);
      next[fieldId] = value;
      return next;
    });
  }

  // Auto-save
  useEffect(function() {
    if (!draftToken || !otpVerified) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(function() {
      draftsAPI.saveDraft(formId, draftToken, values, name).catch(function(e) { /* silent */ });
    }, 5000);
    return function() { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [values, name, draftToken, otpVerified, formId]);

  // Submit
  function handleSubmit() {
    if (!name.trim()) { setError('Please enter your full name'); return; }
    setSubmitting(true);
    setError('');

    var submitData = {
      form_id: formId,
      email: email,
      name: name,
      responses: values,
      participant_type: isScholar ? 'scholar' : 'self_funded',
    };
    if (captchaEnabled) {
      submitData.captcha_token = captchaToken;
      submitData.captcha_answer = captchaAnswer;
    }

    registrationAPI.submit(submitData)
      .then(function() {
        setSubmitted(true);
        setSubmitting(false);
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || 'Submission failed');
        setSubmitting(false);
      });
  }

  // Helpers
  var sections = schema ? (schema.sections || []).slice().sort(function(a,b) { return a.order - b.order; }) : [];
  var totalSteps = sections.length || 1;
  var participantType = isScholar ? 'scholar' : 'self_funded';
  var E = React.createElement;

  // --- Render ---

  if (loading) return E(Box, {sx:{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}, E(CircularProgress));

  if (error && !form) return E(Box, {sx:{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}},
    E(Card, {sx:{maxWidth:500,mx:2}}, E(CardContent, {sx:{p:4,textAlign:'center'}},
      E(Typography, {variant:'h5',color:'error',sx:{mb:2}}, 'Error'),
      E(Typography, null, error))));

  // Submitted
  if (submitted) return E(Box, {sx:{minHeight:'100vh',bgcolor:'#f5f6fa',py:4}},
    E(Container, {maxWidth:'sm'},
      E(Card, null, E(CardContent, {sx:{p:4,textAlign:'center'}},
        E(HowToReg, {sx:{fontSize:64,color:'success.main',mb:2}}),
        E(Typography, {variant:'h4',sx:{mb:1}}, 'Registration Complete!'),
        E(Typography, {color:'text.secondary'},
          isScholar
            ? 'You have been registered as a scholarship recipient.'
            : 'You have been registered as a self-funded participant.'),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mt:2}}, 'A confirmation email will be sent to ' + email)))));

  var formSettings = parsedSettings;
  var bgStyle = formSettings.bg_image_url ? {
    backgroundImage: 'url(' + formSettings.bg_image_url + ')',
    backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
  } : {};

  return E(Box, {sx: Object.assign({minHeight:'100vh', bgcolor:'#f5f6fa', py:4}, bgStyle)},
    E(Container, {maxWidth:'md'},

      // Header
      E(Card, {sx:{mb:3}},
        E(CardContent, {sx:{py:4}},
          formSettings.banner_url ? E(Box, {sx:{mb:2}},
            E('img', {src:formSettings.banner_url, alt:'', style:{width:'100%',maxHeight:200,objectFit:'cover',borderRadius:8}})) : null,
          E(Typography, {variant:'h4',color:'primary',sx:{mb:1}}, form && form.title),
          form && form.description ? E(Typography, {variant:'body1',color:'text.secondary'}, form.description) : null)),

      // OTP verification step
      !otpVerified ? E(Card, null,
        E(CardContent, {sx:{p:4}},

          // Email + name entry
          !otpSent ? E(Box, null,
            E(Typography, {variant:'h6',sx:{mb:2}}, 'Enter your email to register'),
            error ? E(Alert, {severity:'error',sx:{mb:2}}, error) : null,
            otpError ? E(Alert, {severity:'error',sx:{mb:2}}, otpError) : null,
            scholarStatus && !scholarStatus.is_scholar && !scholarStatus.allow_self_fund ?
              E(Alert, {severity:'warning',sx:{mb:2}}, 'Registration is only open for accepted scholars.') : null,
            E(TextField, {fullWidth:true, label:'Email Address', type:'email', value:email, onChange:function(e){setEmail(e.target.value);}, sx:{mb:2}}),
            E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:2}},
              "We'll email you a verification code to confirm your address before you register."),
            E(Button, {variant:'contained', size:'large', fullWidth:true, startIcon:E(Send),
              onClick:checkScholarAndSendOTP, disabled:otpLoading || !email},
              otpLoading ? 'Checking...' : 'Send Verification Code'))

          // OTP entry
          : E(Box, null,
            E(Typography, {variant:'h6',sx:{mb:1}}, 'Verify your email'),
            E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}}, 'A 6-digit code was sent to ' + email),
            otpError ? E(Alert, {severity:'error',sx:{mb:2}}, otpError) : null,
            scholarStatus ? E(Alert, {severity:isScholar?'success':'info', sx:{mb:2}},
              isScholar
                ? 'Welcome! You are registered as a scholarship recipient (' + scholarStatus.scholar_name + ')'
                : 'You will be registered as a self-funded participant.') : null,
            E(TextField, {fullWidth:true, label:'6-digit verification code', value:otp,
              onChange:function(e){setOtp(e.target.value.replace(/\D/g,'').slice(0,6));},
              inputProps:{maxLength:6, style:{textAlign:'center',fontSize:24,letterSpacing:8}}, sx:{mb:2}}),
            E(Button, {variant:'contained', size:'large', fullWidth:true, onClick:verifyOTP,
              disabled:otpLoading || otp.length < 6},
              otpLoading ? 'Verifying...' : 'Verify & Continue'),
            E(Button, {fullWidth:true, sx:{mt:1}, onClick:function(){setOtpSent(false);setOtp('');setOtpError('');}}, 'Back'))
        )
      )

      // Form filling step
      : E(Card, null,
        E(CardContent, {sx:{p:4}},

          // Stepper
          sections.length > 1 ? E(Stepper, {activeStep:currentStep, sx:{mb:3}},
            sections.map(function(sec,i) {
              return E(Step, {key:sec.id},
                E(StepLabel, null, sec.title));
            })) : null,

          // Section title
          sections.length > 0 && sections[currentStep] ?
            E(Typography, {variant:'h6',sx:{mb:2,color:'primary.main'}},
              sections[currentStep] && sections[currentStep].title) : null,

          // Full name — collected after email verification
          currentStep === 0 ? E(TextField, {fullWidth:true, label:'Full Name', value:name, required:true,
            onChange:function(e){setName(e.target.value); if (e.target.value.trim()) setError('');},
            error: !name.trim(),
            helperText: !name.trim() ? 'Please enter your full name to continue' : '',
            sx:{mb:3}}) : null,

          // Fields
          E(MultiStepFormRenderer, {
            schema:schema, values:values, onChange:handleFieldChange,
            errors:fieldErrors, currentStep:currentStep,
            scholarOnly:isScholar
          }),

          error ? E(Alert, {severity:'error',sx:{mt:2,mb:2}}, error) : null,

          // CAPTCHA above buttons
          captchaEnabled && currentStep === totalSteps - 1 ?
            E(Box, {sx:{mb:3,p:2,border:'1px solid',borderColor:'divider',borderRadius:1}},
              E(Typography, {variant:'body2',sx:{mb:1,fontWeight:500}}, captchaQuestion),
              E(TextField, {size:'small',label:'Your answer',value:captchaAnswer,
                onChange:function(e){setCaptchaAnswer(e.target.value);},sx:{width:140}})) : null,

          // Navigation buttons
          E(Box, {sx:{display:'flex',justifyContent:'space-between',mt:2}},
            E(Button, {variant:'outlined', startIcon:E(NavigateBefore),
              onClick:function(){setCurrentStep(function(s){return s-1;});setFieldErrors({});},
              disabled:currentStep === 0}, 'Previous'),

            currentStep < totalSteps - 1
              ? E(Button, {variant:'contained', endIcon:E(NavigateNext),
                  onClick:function(){ if (currentStep === 0 && !name.trim()) { setError('Please enter your full name'); return; } setCurrentStep(function(s){return s+1;}); }}, 'Next')
              : E(Button, {variant:'contained', size:'large', startIcon:E(HowToReg),
                  onClick:handleSubmit, disabled:submitting},
                  submitting ? 'Registering...' : 'Complete Registration')
          )
        )
      )
    )
  );
}
